// The brief (Blue Market only): the brief as given, its files, concept.md, and "Make the first round with Claude".
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post, put } from '../api';
import { useAuth } from '../auth';
import { Modal, ago, useConfirm, useToast } from '../ui';
import type { Brief, BriefDoc, ClaudeRun, Project } from '../types';
import { ClaudeRunPanel } from './Spec';
import { uploadFile } from './RichText';

const size = (n: number) => n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
const active = (r: ClaudeRun | null | undefined) => r?.status === 'queued' || r?.status === 'running';

export function useBrief(projectId: string, enabled = true) {
  return useQuery({ queryKey: ['brief', projectId], queryFn: () => get<Brief>(`/api/projects/${projectId}/brief`), enabled });
}

function DocBlock({ project, doc, which, title, empty, canEdit, locked, startOpen }: {
  project: Project; doc: BriefDoc; which: 'brief' | 'concept'; title: string; empty: string; canEdit: boolean; locked: boolean; startOpen: boolean;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const [open, setOpen] = useState(startOpen);
  const save = useMutation({
    mutationFn: () => put<Brief>(`/api/projects/${project.id}/brief/${which}`, { text: draft, version: doc.version }),
    onSuccess: (b) => { qc.setQueryData(['brief', project.id], b); setDraft(null); toast(`Saved ${which === 'brief' ? 'the brief' : 'concept.md'}`); },
    onError: (e: Error) => toast(e.message, true),
  });
  const has = doc.text.trim().length > 0;
  return (
    <div className="brief-block">
      <div className="row wrap" style={{ gap: 8 }}>
        <b className="small">{title}</b>
        {doc.updated_at && <span className="muted tiny">updated {ago(doc.updated_at)}</span>}
        <span className="grow" />
        {has && draft === null && <button className="btn sm ghost" onClick={() => setOpen(!open)} aria-expanded={open}>{open ? 'Hide' : 'Show'}</button>}
        {canEdit && draft === null && <button className="btn sm" disabled={locked} title={locked ? 'Claude is working from the brief' : ''}
                                                onClick={() => { setDraft(doc.text); setOpen(true); }}>{has ? 'Edit' : which === 'brief' ? 'Write the brief' : 'Write'}</button>}
      </div>
      {draft !== null ? (
        <form className="stack" style={{ marginTop: 8 }} onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
          <textarea className="input" rows={14} value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus spellCheck aria-label={title}
                    placeholder={which === 'brief' ? 'Paste the brief as the client gave it: emails, call notes, goals, users, must-haves, deadlines, links. Markdown works.' : ''} />
          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn ghost" onClick={() => setDraft(null)}>Cancel</button>
            <button className="btn primary" disabled={save.isPending || draft === doc.text}>{save.isPending && <span className="spin" />}Save</button>
          </div>
        </form>
      ) : has ? (open && <div className="prose brief-doc" dangerouslySetInnerHTML={{ __html: doc.html }} />)
        : <p className="muted tiny" style={{ margin: '4px 0 0' }}>{empty}</p>}
    </div>
  );
}

function BriefFiles({ project, data, canEdit, locked }: { project: Project; data: Brief; canEdit: boolean; locked: boolean }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const refresh = () => qc.invalidateQueries({ queryKey: ['brief', project.id] });
  const add = async (list: FileList | null) => {
    const files = Array.from(list ?? []);
    if (input.current) input.current.value = '';
    for (const f of files) {
      setUploading((n) => n + 1);
      try { await uploadFile(`/api/projects/${project.id}/brief/files`, f); } catch (e) { toast(`${f.name}: ${(e as Error).message}`, true); }
      setUploading((n) => n - 1);
    }
    refresh();
  };
  const remove = async (name: string) => {
    if (!await confirm(`Remove ${name}?`, { body: 'It is deleted from the brief folder.', ok: 'Remove', danger: true })) return;
    try { qc.setQueryData(['brief', project.id], await del<Brief>(`/api/projects/${project.id}/brief/files/${encodeURIComponent(name)}`)); } catch (e) { toast((e as Error).message, true); }
  };
  return (
    <div className="brief-block">
      <div className="row wrap" style={{ gap: 8 }}>
        <b className="small">Files</b>
        <span className="muted tiny">PDFs, images and text files, up to {data.max_mb} MB each. Save Word or PowerPoint files as PDF first.</span>
        <span className="grow" />
        {canEdit && <>
          <input ref={input} type="file" multiple hidden accept={data.extensions.join(',')} onChange={(e) => add(e.target.files)} />
          <button className="btn sm" disabled={locked || uploading > 0} onClick={() => input.current?.click()}>{uploading > 0 && <span className="spin" />}Add files</button>
        </>}
      </div>
      {data.files.length > 0 && (
        <ul className="brief-files">
          {data.files.map((f) => (
            <li key={f.name}>
              <a href={`/api/projects/${project.id}/brief/files/${encodeURIComponent(f.name)}`} download className="grow">{f.name}</a>
              <span className="muted tiny">{size(f.size)}</span>
              {canEdit && <button className="btn sm ghost" disabled={locked} onClick={() => remove(f.name)} aria-label={`Remove ${f.name}`}>Remove</button>}
            </li>
          ))}
        </ul>
      )}
      {dialog}
    </div>
  );
}

function FirstRoundDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [instructions, setInstructions] = useState('');
  const start = useMutation({
    mutationFn: () => post<ClaudeRun>(`/api/projects/${project.id}/first-round/claude`, { instructions }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['brief', project.id] }); qc.invalidateQueries({ queryKey: ['rounds', project.id] }); onClose(); },
    onError: (e: Error) => toast(e.message, true),
  });
  return (
    <Modal title="Make the first round with Claude" onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); start.mutate(); }}>
        <p className="small" style={{ margin: 0 }}>
          Claude reads the brief and its files, writes concept.md (the brief plus implied needs and unknowns) if it's still empty, and designs v1:
          usually 10 to 16 screens, with notes and open questions. A second Claude then reviews the round and the first fixes what it finds.
        </p>
        <p className="muted small" style={{ margin: 0 }}>It usually takes 20 to 60 minutes and runs in the background: you can leave this page. v1 starts as internal.</p>
        <label className="field"><span>Instructions (optional)</span>
          <textarea className="input" rows={4} value={instructions} onChange={(e) => setInstructions(e.target.value)} maxLength={4000}
                    placeholder="e.g. A phone app for shoppers plus a web back office for merchants. Leave payments out of this round." />
        </label>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={start.isPending}>{start.isPending && <span className="spin" />}Start</button>
        </div>
      </form>
    </Modal>
  );
}

export function BriefSection({ project }: { project: Project }) {
  const { can } = useAuth();
  const { data } = useBrief(project.id);
  const [asking, setAsking] = useState(false);
  const [open, setOpen] = useState<boolean | null>(null);
  const canEdit = can('delivery.edit');
  const canRun = canEdit && can('claude.run');
  const running = active(data?.run);
  useEffect(() => { if (running) setOpen(true); }, [running]);
  if (!data) return null;
  const expanded = open ?? !data.has_rounds;       // folded away once the project has rounds
  const locked = running;

  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <h2>Brief</h2>
        <div className="row" style={{ gap: 8 }}>
          <span className="muted tiny mono">{project.path}/brief</span>
          {data.has_rounds && <button className="btn sm ghost" onClick={() => setOpen(!expanded)} aria-expanded={expanded}>{expanded ? 'Hide' : 'Show'}</button>}
        </div>
      </div>
      {!expanded ? (
        <div className="round-row muted tiny">
          {data.brief.text.trim() ? 'Brief' : 'No brief'}{data.files.length ? ` · ${data.files.length} file${data.files.length === 1 ? '' : 's'}` : ''}
          {data.concept.written ? ` · concept.md${data.concept.updated_at ? ` updated ${ago(data.concept.updated_at)}` : ''}` : ''}
        </div>
      ) : (
        <>
          {!data.has_rounds && <p className="small" style={{ margin: 0, padding: '12px 18px 0' }}>
            Write or paste the brief and add any documents. Claude then writes the concept and makes the first design round from it.
          </p>}
          <div className="stack" style={{ padding: '12px 18px', gap: 14 }}>
            <DocBlock project={project} doc={data.brief} which="brief" title="The brief as given" canEdit={canEdit} locked={locked} startOpen={!data.has_rounds}
                      empty="Nothing yet. Paste the client's brief, emails or call notes." />
            <BriefFiles project={project} data={data} canEdit={canEdit} locked={locked} />
            <DocBlock project={project} doc={data.concept} which="concept" title="Concept (concept.md)" canEdit={canEdit && data.concept.written} locked={locked} startOpen={false}
                      empty="Claude writes it from the brief when it makes the first round: the brief, the needs it implies, and the unknowns." />
          </div>
          {!data.has_rounds && canRun && (
            <div className="round-row">
              <div className="grow muted tiny" style={{ minWidth: 180 }}>{data.blocked && !running ? data.blocked + '.' : 'Claude makes v1 from the brief, then a second Claude reviews it.'}</div>
              <button className="btn primary sm" disabled={!!data.blocked} onClick={() => setAsking(true)}>Make the first round with Claude</button>
            </div>
          )}
        </>
      )}
      {data.run && expanded && <div style={{ padding: '12px 18px', borderTop: '1px solid var(--line)' }}><ClaudeRunPanel run={data.run} canStop={canRun} /></div>}
      {asking && <FirstRoundDialog project={project} onClose={() => setAsking(false)} />}
    </section>
  );
}
